import { CircularProgress, FormControl } from "@mui/material";
import * as React from "react";
import { useEffect, useMemo, useRef } from "react";

import Select from "./Select";
import useLoader from "../../hooks/useLoader";
import { where } from "../../functions/linqSimulation";

function pickScalar(row, key) {
  if (row == null || key == null) return undefined;
  if (row[key] !== undefined && row[key] !== null) return row[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  if (row[pascal] !== undefined && row[pascal] !== null) return row[pascal];
  return undefined;
}

function defaultNormalizeOption(row, optionValueKey, optionLabelKey) {
  const v = pickScalar(row, optionValueKey);
  const l = pickScalar(row, optionLabelKey);
  return { ...row, [optionValueKey]: v, [optionLabelKey]: l };
}

/**
 * Select that loads options via crud.search(query) (no Redux).
 * Optional server projection: query.select = { members: ["Id", "Name"] }.
 */
const DomainSelect2 = ({
  crud,
  query,
  isNullable,
  skipFetch,
  filter,
  postProcessOptions,
  normalizeOption,
  optionValueKey = "id",
  optionLabelKey = "name",
  ...props
}) => {
  const { loading, start, stop } = useLoader(false);
  const [fetched, setFetched] = React.useState([]);
  const normalizeOptionRef = useRef(normalizeOption);
  normalizeOptionRef.current = normalizeOption;

  const queryKey = useMemo(() => JSON.stringify(query ?? {}), [query]);

  useEffect(() => {
    if (skipFetch || !crud?.search) {
      setFetched([]);
      return;
    }

    let active = true;
    (async () => {
      start();
      try {
        const data = await crud.search(query);
        if (!active) return;
        let list = data?.result || [];
        const norm = normalizeOptionRef.current;
        list = list.map((row) =>
          norm ? norm(row) : defaultNormalizeOption(row, optionValueKey, optionLabelKey)
        );
        setFetched(list);
      } catch (e) {
        console.error("DomainSelect2 search error:", e);
        if (active) setFetched([]);
      } finally {
        if (active) stop();
      }
    })();

    return () => {
      active = false;
    };
  }, [crud, queryKey, skipFetch, optionValueKey, optionLabelKey, start, stop]);

  const items = useMemo(() => {
    let list = fetched;
    if (isNullable) {
      const emptyOption = {};
      emptyOption[optionValueKey] = null;
      emptyOption[optionLabelKey] = "Не указано";
      list = [emptyOption, ...list];
    }
    if (filter) {
      list = where(list, filter);
    }
    if (postProcessOptions) {
      list = postProcessOptions(list);
    }
    return list;
  }, [
    fetched,
    isNullable,
    filter,
    postProcessOptions,
    optionValueKey,
    optionLabelKey,
  ]);

  return (
    <FormControl
      sx={props?.formStyle}
      disabled={props.disabled}
      margin={props.margin || "normal"}
      fullWidth
    >
      {loading ? (
        <CircularProgress color="secondary" sx={{ alignSelf: "center" }} />
      ) : (
        <Select
          options={items}
          searchable={props.searchable}
          optionValueKey={optionValueKey}
          optionLabelKey={optionLabelKey}
          {...props}
        />
      )}
    </FormControl>
  );
};

export default DomainSelect2;
